import type { Status as UiStatus } from "@gui-creator/gemeinsam";
import { ROLLEN_ANZEIGE } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, History, ListTodo, LogOut, Menu, Moon, Settings, Sun, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink, useLocation, useSearchParams } from "react-router";
import { schluessel, useStruktur, useUi, type Konto } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { STATUS_ANZEIGE, StatusMarke } from "../baum/StatusMarke.tsx";
import { Anwesenheit, Speicherstand } from "../live/Anwesenheit.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import { Marke } from "../ui/Marke.tsx";
import { MenueKnopf } from "../ui/Menue.tsx";
import { FreigebenKnopf, StandAnzeige } from "../planung/Freigabe.tsx";
import { farbschemaSetzen, useWirksamesFarbschema } from "../stil/farbschema.ts";
import { useAbmelden, useDarf } from "./konto.ts";
import { findePfad } from "./pfad.ts";

const FREI: UiStatus[] = ["idee", "in_arbeit", "bereit"];
const SEITEN: Record<string, string> = { todos: "Offene To-dos", papierkorb: "Papierkorb", einstellungen: "Einstellungen" };

function StatusWahl({ uiId }: { uiId: string }) {
  const ui = useUi(uiId);
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darf = useDarf("bearbeiten");
  if (!ui.data) return null;
  const status = ui.data.status;
  const setzen = async (neu: UiStatus) => {
    try {
      await api(`/api/uis/${uiId}/status`, { methode: "POST", body: { status: neu } });
      await Promise.all([qc.invalidateQueries({ queryKey: schluessel.ui(uiId) }), qc.invalidateQueries({ queryKey: schluessel.struktur })]);
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    }
  };
  return (
    <label className="status-wahl" title="Status der UI">
      <StatusMarke status={status} />
      <span className="unsichtbar">Status</span>
      <select value={status} disabled={!darf} onChange={(e) => void setzen(e.target.value as UiStatus)}>
        {!FREI.includes(status) && (
          <option value={status} disabled>
            {STATUS_ANZEIGE[status].text}
          </option>
        )}
        {FREI.map((s) => (
          <option key={s} value={s}>
            {STATUS_ANZEIGE[s].text}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Kopfleiste({ konto, links, rechts, onMenue }: { konto: Konto; links?: ReactNode; rechts?: ReactNode; onMenue?: () => void }) {
  const ort = useLocation();
  const struktur = useStruktur();
  const [suche, setSuche] = useSearchParams();
  const verlaufOffen = suche.get("ansicht") === "verlauf";
  const verlaufUmschalten = (an = !verlaufOffen) => {
    const neu = new URLSearchParams(suche);
    if (an) neu.set("ansicht", "verlauf");
    else neu.delete("ansicht");
    setSuche(neu);
  };
  const teile = ort.pathname.split("/").filter(Boolean);
  const pfad = teile[0] === "p" && struktur.data ? findePfad(struktur.data, { plugin: teile[1], workspace: teile[2], ui: teile[3] }) : null;

  const abmelden = useAbmelden();
  const schema = useWirksamesFarbschema();

  type Glied = { text: string; zu: string | null };
  const katalogPlugin = teile[0] === "katalog" ? struktur.data?.plugins.find((p) => p.schluessel === teile[1]) : undefined;
  let glieder: Glied[] = [];
  if (katalogPlugin) glieder = [{ text: katalogPlugin.name, zu: `/p/${katalogPlugin.schluessel}` }, { text: "Katalog", zu: null }];
  else if (teile.length === 1 && SEITEN[teile[0]!]) glieder = [{ text: SEITEN[teile[0]!]!, zu: null }];
  else if (pfad)
    glieder = [
      pfad.plugin && { text: pfad.plugin.name, zu: `/p/${pfad.plugin.schluessel}` },
      pfad.workspace && pfad.plugin && { text: pfad.workspace.name, zu: `/p/${pfad.plugin.schluessel}/${pfad.workspace.schluessel}` },
      pfad.ui && { text: pfad.ui.name ?? pfad.ui.schluessel, zu: null },
    ].filter((g): g is Glied => !!g);

  return (
    <header className="kopfleiste">
      <div className="kopf-links">
        {onMenue && (
          <button type="button" className="knopf knopf-still knopf-symbol kopf-menue" aria-label="Struktur öffnen" onClick={onMenue}>
            <Menu size={16} aria-hidden="true" />
          </button>
        )}
        <Link to="/" className="kopf-marke" aria-label="Startseite">
          <Marke groesse={22} />
        </Link>
        <nav className="kopf-pfad" aria-label="Pfad">
          {glieder.map((g, i) => (
            <span key={i} className="kopf-glied">
              {i > 0 && <ChevronRight size={12} className="kopf-trenner" aria-hidden="true" />}
              {g.zu ? (
                <Link to={g.zu}>{g.text}</Link>
              ) : (
                <span aria-current="page" className="kopf-hier">
                  {g.text}
                </span>
              )}
            </span>
          ))}
        </nav>
        {pfad?.ui && <StatusWahl uiId={pfad.ui.id} />}
        {pfad?.ui && <StandAnzeige uiId={pfad.ui.id} onKlick={() => verlaufUmschalten(true)} />}
        {pfad?.ui && <Speicherstand />}
        {links}
      </div>
      <div className="kopf-rechts">
        {rechts}
        {pfad?.ui && <Anwesenheit ui={pfad.ui.id} ich={konto.kontoId} />}
        {pfad?.ui && (
          <button type="button" className="knopf knopf-still knopf-symbol kopf-verlauf" aria-label="Verlauf" title="Verlauf und Stände" aria-pressed={verlaufOffen} onClick={() => verlaufUmschalten()}>
            <History size={15} aria-hidden="true" />
          </button>
        )}
        {pfad?.ui && <FreigebenKnopf uiId={pfad.ui.id} />}
        <div className="kopf-nav">
          <NavLink to="/todos" className="knopf knopf-still knopf-symbol" aria-label="Offene To-dos" title="Offene To-dos">
            <ListTodo size={15} aria-hidden="true" />
          </NavLink>
          <NavLink to="/papierkorb" className="knopf knopf-still knopf-symbol" aria-label="Papierkorb" title="Papierkorb">
            <Trash2 size={15} aria-hidden="true" />
          </NavLink>
          <NavLink to="/einstellungen" className="knopf knopf-still knopf-symbol" aria-label="Einstellungen" title="Einstellungen">
            <Settings size={15} aria-hidden="true" />
          </NavLink>
        </div>
        <MenueKnopf
          label={`Konto ${konto.name}`}
          className="kopf-konto"
          punkte={[
            { text: `${konto.name} · ${ROLLEN_ANZEIGE[konto.rolle]}`, deaktiviert: true, aktion: () => {} },
            "trenner",
            schema === "dunkel"
              ? { text: "Helle Darstellung", symbol: <Sun size={14} />, aktion: () => farbschemaSetzen("hell") }
              : { text: "Dunkle Darstellung", symbol: <Moon size={14} />, aktion: () => farbschemaSetzen("dunkel") },
            { text: "Abmelden", symbol: <LogOut size={14} />, aktion: () => void abmelden() },
          ]}
        >
          <span className="kopf-kuerzel" aria-hidden="true">
            {konto.name.slice(0, 1).toUpperCase()}
          </span>
        </MenueKnopf>
      </div>
    </header>
  );
}
